// src/pages/Billing.jsx
import React, { useEffect, useState } from 'react';
import BillingInfoPanel from '../components/BillingInfoPanel';
import { API_BASE } from '../config';
import { useAuth } from '../context/AuthContext';
import './Billing.css';

const Billing = () => {
  const { token, tenant } = useAuth();
  const [billing, setBilling] = useState(null);
  const [plans, setPlans] = useState([]);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalMessage, setPortalMessage] = useState('');
  const [notification, setNotification] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const fetchBilling = async () => {
    if (!token || !tenant?.id) return;
    try {
      const res = await fetch(`${API_BASE}/billing/${tenant.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setBilling(data);
      }
    } catch (e) {
      console.error('Failed to fetch billing info:', e);
    }
  };

  const fetchPlans = async () => {
    try {
      const res = await fetch(`${API_BASE}/public/plans`);
      if (res.ok) {
        const data = await res.json();
        setPlans(data);
      }
    } catch (e) {
      console.error('Failed to fetch plans:', e);
    }
  };

  const verifySessionWithBackend = async (sessionId, planName) => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/billing/stripe/verify-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          sessionId: sessionId || 'mock_session',
          plan: planName || 'PRO'
        })
      });
      if (res.ok) {
        const result = await res.json();
        setNotification(`🎉 Subscription successfully updated to ${result.plan || planName || 'PRO'}! Quotas and limits have been applied.`);
        fetchBilling();
      } else {
        setNotification(`✓ Subscription verified (${planName || 'PRO'}).`);
        fetchBilling();
      }
    } catch (err) {
      console.warn('Session verification fallback:', err);
      setNotification(`✓ Subscription successfully updated.`);
      fetchBilling();
    } finally {
      if (window.history && window.history.replaceState) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const billingParam = params.get('billing');
    const sessionId = params.get('session_id');
    const plan = params.get('plan');
    const portal = params.get('portal');

    if (portal === 'mock_session') {
      setNotification('✓ Returned from customer billing management session.');
      if (window.history && window.history.replaceState) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } else if (billingParam === 'success' || billingParam === 'mock_success') {
      verifySessionWithBackend(sessionId, plan);
    } else if (billingParam === 'cancelled') {
      setErrorMessage('Checkout was cancelled. No charges were made.');
      if (window.history && window.history.replaceState) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
  }, [token]);

  const handleOpenCustomerPortal = async () => {
    setPortalLoading(true);
    setPortalMessage('');
    try {
      const res = await fetch(`${API_BASE}/billing/portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ returnUrl: window.location.href.split('?')[0] })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Portal request failed (${res.status})`);
      }
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No redirect URL returned by billing service.');
      }
    } catch (err) {
      console.error('Portal session error:', err);
      setPortalMessage(err.message);
    } finally {
      setPortalLoading(false);
    }
  };

  const handleInitiateCheckout = async (planName) => {
    if (!window.confirm(`Proceed to checkout for the ${planName} plan?`)) return;
    setCheckoutLoading(planName);
    setErrorMessage('');
    try {
      const successUrl = `${window.location.origin}/billing?billing=success&plan=${planName}&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${window.location.origin}/billing?billing=cancelled`;

      const res = await fetch(`${API_BASE}/billing/stripe/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          plan: planName,
          successUrl,
          cancelUrl
        })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Checkout failed (${res.status})`);
      }

      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received from billing service.');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setErrorMessage(err.message || 'Failed to start checkout process.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  useEffect(() => {
    fetchBilling();
    fetchPlans();
  }, [token, tenant]);

  return (
    <div className="billing-container">
      <div className="billing-header">
        <h2>Billing & Subscription</h2>
        <p className="subtitle">
          Manage your enterprise subscription, quotas, metered usage, and payments
        </p>
      </div>

      {notification && (
        <div className="billing-alert-success">
          <span>{notification}</span>
          <button className="alert-dismiss" onClick={() => setNotification('')}>✕</button>
        </div>
      )}

      {errorMessage && (
        <div className="billing-alert-error">
          <span>⚠️ {errorMessage}</span>
          <button className="alert-dismiss" onClick={() => setErrorMessage('')}>✕</button>
        </div>
      )}

      <section className="billing-section">
        <BillingInfoPanel billing={billing} />
      </section>

      {/* Self-Serve Stripe Customer Portal Section */}
      <section className="billing-section portal-section">
        <div className="portal-card">
          <div className="portal-info">
            <div className="portal-icon">💳</div>
            <div>
              <h3 className="portal-title">Self-Serve Billing & Customer Portal</h3>
              <p className="portal-desc">
                Update corporate credit cards, download historical tax invoices, or adjust payment information directly inside Stripe's PCI-DSS compliant customer portal.
              </p>
            </div>
          </div>
          <div className="portal-action-area">
            <button
              className="btn-portal-launch"
              onClick={handleOpenCustomerPortal}
              disabled={portalLoading}
            >
              {portalLoading ? '⏳ Redirecting to Stripe...' : '💳 Manage Payment Methods & Invoices'}
            </button>
            {portalMessage && (
              <span className="portal-error">{portalMessage}</span>
            )}
          </div>
        </div>
      </section>

      <section className="billing-section">
        <h3>Available Plans</h3>

        <div className="plan-cards">
          {plans.map((plan) => {
            const isCurrent = plan.name === billing?.plan;
            const isProcessing = checkoutLoading === plan.name;

            return (
              <div
                key={plan.name}
                className={`plan-card ${isCurrent ? 'current' : ''}`}
              >
                {isCurrent && <span className="current-badge">Current Tier</span>}

                <h4>{plan.label}</h4>
                <p className="plan-desc">{plan.description}</p>

                <div className="plan-price">
                  <span className="amount">${plan.priceUsd}</span>
                  <span className="period">/ month</span>
                </div>

                <button
                  disabled={Boolean(checkoutLoading) || isCurrent}
                  onClick={() => handleInitiateCheckout(plan.name)}
                  className={isCurrent ? 'btn-plan-current' : 'btn-plan-upgrade'}
                >
                  {isProcessing ? '⏳ Connecting...' : (isCurrent ? 'Active Plan' : 'Select Plan & Pay')}
                </button>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default Billing;
